import { FC, useContext, useState } from 'react';

import { useContent } from '@eneco/cms';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import { typedYupResolver } from '@eneco/hooks/form';
import { Button, Form, Heading, InputEmail, Stack, Text, TextLink, NavLink } from '@eneco/sparky';
import { ChevronLeftIcon, InfoIcon, PlusIcon, MinusIcon } from '@eneco/sparky/icons';
import { TrackViewComponent } from '@eneco/tracking';
import { SubmitHandler, useForm } from 'react-hook-form';

import { RegistrationAccountContent } from '../registrationFlow.types';
import { registerOrgEmailSchema } from './registerOrgEmailSchema';
import { Action } from '../registrationFlow.constants';
import { RegistrationMachine } from '../registrationFlow.machine';

const resolver = typedYupResolver<FormValues>(registerOrgEmailSchema);

type FormValues = {
  emailAddress: string;
  primaryEmailOverride: string;
};

export const RegisterOrgEmail: FC = () => {
  const content = useContent<RegistrationAccountContent>();
  const { send, state } = useContext(RegistrationMachine);
  const [showPrimaryEmailOverride, setShowPrimaryEmailOverride] = useState<boolean | null>(
    state.context.primaryEmailOverride ? true : null,
  );
  const { hasError } = state.context;
  const {
    formState: { errors },
    handleSubmit,
    register,
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      emailAddress: state.context.emailAddress,
      primaryEmailOverride: state.context.primaryEmailOverride || '',
    },
    resolver,
  });
  const handleBack = () => send({ type: Action.BACK });

  const submitForm: SubmitHandler<FormValues> = ({ emailAddress, primaryEmailOverride }) => {
    send({ type: Action.CUSTOMER_SET_EMAIL, emailAddress, primaryEmailOverride });
  };

  const handleClickSetPrimairyEmailOverride = (setOveride: boolean) => {
    setShowPrimaryEmailOverride(setOveride);

    if (setOveride === false) {
      setValue('primaryEmailOverride', '');
    }
  };

  const formErrorMessages: { [key: string]: string } = {
    emailInvalid: content.RegisterOrgEmail_emailFormField?.validationMessage ?? '',
    emailRequired: content.RegisterOrgEmail_emailFormField?.requiredMessage ?? '',
    emailOverrideInvalid: content.RegisterOrgEmail_emailFormField?.validationMessage ?? '',
  };

  return (
    <Form onSubmit={handleSubmit(submitForm)}>
      <Stack gap="6">
        <NavLink variant="secondary" onClick={handleBack} leftIcon={<ChevronLeftIcon />}>
          {content.RegisterOrgEmail_backLinkText}
        </NavLink>
        <Heading as="h1" size="XS">
          {content.RegisterOrgEmail_title}
        </Heading>
        {hasError && (
          <TrackedNotificationBox
            label={content.RegisterOrgEmail_technicalErrorNotificationField?.title}
            title={content.RegisterOrgEmail_technicalErrorNotificationField?.title ?? ''}
            isAlert
            text={<RichText html={content.RegisterOrgEmail_technicalErrorNotificationField?.content} />}
            variant="error"
          />
        )}
        <Stack gap="3">
          <RichText
            html={
              showPrimaryEmailOverride
                ? content.overrideDescription
                : content.introductionDescription
            }
          />
          <TrackedDialog
            trigger={
              <TextLink rightIcon={<InfoIcon size="small" />}>
                {content.RegisterOrgEmail_modalDialog?.triggerText}
              </TextLink>
            }
            title={
              showPrimaryEmailOverride
                ? content.overrideEmailForLoginCodeModalDialog?.title
                : content.RegisterOrgEmail_modalDialog?.title
            }>
            <TrackViewComponent type="dialog" label={content.RegisterOrgEmail_modalDialog?.title ?? ''}>
              <Stack gap="4">
                <RichText
                  html={
                    showPrimaryEmailOverride
                      ? content.overrideEmailForLoginCodeModalDialog?.content
                      : content.RegisterOrgEmail_modalDialog?.content
                  }
                />
              </Stack>
            </TrackViewComponent>
          </TrackedDialog>
        </Stack>
        <Stack gap="3">
          <InputEmail
            {...register('emailAddress')}
            label={
              (showPrimaryEmailOverride
                ? content.overrideEmailForUsernameLabel
                : content.RegisterOrgEmail_emailFormField?.label) ?? ''
            }
            autoComplete="email"
            error={errors?.emailAddress?.message && formErrorMessages[errors.emailAddress.message]}
            placeholder={content.RegisterOrgEmail_emailFormField?.placeholder}
            hint={showPrimaryEmailOverride ? content.overrideEmailForUsernameHintLabel : ''}
            defaultValue={state.context.emailAddress}
          />
          {!showPrimaryEmailOverride && (
            <Text>
              <TextLink
                emphasis="medium"
                onClick={() => {
                  handleClickSetPrimairyEmailOverride(true);
                }}>
                <PlusIcon color="iconPrimary" />
                {content.emailOverrideLinkText}
              </TextLink>
            </Text>
          )}
        </Stack>

        {showPrimaryEmailOverride && (
          <Stack gap="2">
            <InputEmail
              {...register('primaryEmailOverride')}
              label={content.overrideEmailForLoginCodeLabel ?? ''}
              autoComplete="email"
              error={errors?.primaryEmailOverride?.message && formErrorMessages[errors.primaryEmailOverride.message]}
              placeholder={content.RegisterOrgEmail_emailFormField?.placeholder}
              hint={content.overrideEmailForLoginCodeHintLabel}
              defaultValue={state.context.primaryEmailOverride || ''}
            />
            <Text>
              <TextLink
                emphasis="medium"
                onClick={() => {
                  handleClickSetPrimairyEmailOverride(false);
                }}>
                <MinusIcon color="iconPrimary" />
                {content.emailOverrideCloseLinkText}
              </TextLink>
            </Text>
          </Stack>
        )}

        <Stack gap="2">
          <Button type="submit">{content.RegisterOrgEmail_confirmButtonText}</Button>
        </Stack>
      </Stack>
    </Form>
  );
};
